import { Component, Input, OnInit } from '@angular/core';
import { IFilter, PageSize } from 'src/app/core/models/base';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { map, Observable, shareReplay } from 'rxjs';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { ITenantSettings } from 'src/app/setting/tenant-settings/models/tenant-setting';
import { overtimeFlexTimeFallbackHours } from 'src/app/shared/helpers/overtime';
import { IEmployee } from 'src/app/employee/employee';
import { AbsenceState, AbsenceType, ICreateAbsenceGroupRequest } from '../../models/absence-group';
import { AbsenceGroupStoreService } from '../../storage/absence-group-store.service';
import { AbsenceGroupService } from '../../services/absence-group.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'app-all-absences',
    templateUrl: './all-absences.component.html',
    providers: [AbsenceGroupStoreService],
    standalone: false
})
export class AllAbsencesComponent implements OnInit {

  constructor(
    private absenceGroupStore: AbsenceGroupStoreService,
    private absenceGroupService: AbsenceGroupService,
    private tenantSettingsStore: TenantSettingsStoreService
  ) { }

  absenceGroups$ = this.absenceGroupStore.absenceGroups$;

  createAbsenceForm : UntypedFormGroup;

  filterForm : UntypedFormGroup;

  // The list (filter + grid) is reserved for a supervisor function or SearchAll, resolved by the
  // backend in the parent. The calendar is open to everyone; the backend redacts it instead.
  @Input() canViewList = false;

  // Toggle between the list (filter + grid) and the calendar (scheduler) view. The calendar is the
  // default: it is what every user gets, the list only exists for those with canViewList.
  viewMode: 'list' | 'calendar' = 'calendar';

  // Whether the "record absence" popup is open.
  createOpen = false;

  // Overtime compensation only exists while the tenant runs overtime.
  absenceTypes$ = this.tenantSettingsStore.tenantSettings$.pipe(
    map(tenantSettings => enumToArray(AbsenceType, "absenceGroups.absenceTypes.")
      .filter(absenceType => tenantSettings?.hasOvertime || absenceType.id !== AbsenceType.OvertimeCompensation))
  );
  absenceStatesArray = enumToArray(AbsenceState, "absenceGroups.absenceStates.")

  tenantSettings$ = this.tenantSettingsStore.tenantSettings$;

  // Only the employees the current user is authorised for: their own supervisor chain, or everyone
  // with SearchAll. Feeds both the list filter and the "record absence" dialog. The calendar view
  // deliberately shows every employee (own resource list); outside the viewer's reach the backend
  // reduces an absence to "absent" without type or title.
  public employees$ : Observable<IEmployee[]> =
  this.absenceGroupService.getVisibleEmployees().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  get absenceTypes(){
    return AbsenceType;
  }

  get pageSizes() {
    return PageSize;
  }

  defaultAbsence = {
    id: null,
    name: 'Bitte wählen',
  }

  defaultEmployee = {
    id: null,
    name: 'Bitte wählen',
    firstName: 'Bitte wählen',
    lastName: null
  }


  ngOnInit(): void {
    // The list search is scoped to the supervisor chain and only shown with canViewList; the
    // grid starts out empty otherwise, so there is nothing to load for a plain user.
    if (this.canViewList) {
      this.absenceGroupStore.loadAbsenceGroups();
    }
    this.initFilterForm();
    this.initCreateAbsenceGroupForm();
  }

  initCreateAbsenceGroupForm(){
    this.createAbsenceForm = new UntypedFormGroup({
      title:  new UntypedFormControl(null),
      dateFrom:  new UntypedFormControl(new Date(), Validators.required),
      isHalfDayStart:  new UntypedFormControl(false),
      dateTo:  new UntypedFormControl(new Date(), Validators.required),
      isHalfDayEnd:  new UntypedFormControl(false),
      absenceType:  new UntypedFormControl(null, Validators.required),
      employee:  new UntypedFormControl(null, Validators.required),
    })

    // update the dateTo if it hasnt been changed to the value of the DateFrom
    this.createAbsenceForm.get('dateFrom').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(dateForm => {
        const dateToControl = this.createAbsenceForm.get('dateTo');
        if(dateToControl.value?.toDateString() == new Date().toDateString()){
          dateToControl.patchValue(dateForm);
        }
      });
  }

  initFilterForm(){
    this.filterForm = new UntypedFormGroup({
      employeeId: new UntypedFormControl(null),
      onlyUnsigned: new UntypedFormControl(false),
      absenceType: new UntypedFormControl(null),
      absenceState: new UntypedFormControl(null),
      from: new UntypedFormControl(new Date(new Date().getFullYear(), 0, 1)),
      to: new UntypedFormControl(new Date(new Date().getFullYear(), 11, 31)),
    })
  }

  onGridReload(){
    this.absenceGroupStore.loadAbsenceGroups();
  }

  onViewModeChange(viewMode: 'list' | 'calendar'){
    this.viewMode = viewMode;
  }

  openCreate(){
    // Always start the popup from a clean form with today's dates prefilled.
    this.createAbsenceForm.reset({
      title: null,
      dateFrom: new Date(),
      isHalfDayStart: false,
      dateTo: new Date(),
      isHalfDayEnd: false,
      absenceType: null,
      employee: null,
    });
    this.createOpen = true;
  }

  closeCreate(){
    this.createOpen = false;
  }

  onCreateAbsence(){
    const formValues = this.createAbsenceForm.value;

    const request: ICreateAbsenceGroupRequest = {
      title:  formValues.title,
      dateFrom: new Date(formValues.dateFrom).toDateString(),
      isHalfDayStart: formValues.isHalfDayStart,
      dateTo: new Date(formValues.dateTo).toDateString(),
      isHalfDayEnd:  formValues.isHalfDayEnd,
      absenceType:  formValues.absenceType,
      employeeId:  formValues.employee.id,
    }
    this.absenceGroupStore.createAbsenceGroup(request);
    this.closeCreate();
  }


  // Hours of the requested overtime compensation that the balance does not cover. Only a hint -
  // saving stays possible.
  flexTimeFallbackHours(tenantSettings: ITenantSettings): number {
    const formValues = this.createAbsenceForm?.value;

    if(!tenantSettings?.hasOvertime || formValues?.absenceType !== AbsenceType.OvertimeCompensation){
      return 0;
    }

    return overtimeFlexTimeFallbackHours(
      formValues.employee, tenantSettings, formValues.dateFrom, formValues.dateTo,
      formValues.isHalfDayStart, formValues.isHalfDayEnd
    );
  }

  onFilter(){
    const formValues = this.filterForm.value;

    delete this.absenceGroupStore.searchAbseceGroupRequest['advancedFilter'];
    this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter = {}
    this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.logic = "and";
    this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters = [];

    if(formValues.employeeId){
      const employeeId = formValues.employeeId;
      const filter : IFilter = {
        field: 'employeeId',
        operator: 'eq',
        value: employeeId
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.onlyUnsigned){
      const filter : IFilter = {
        field: 'absenceState',
        operator: 'eq',
        value: '0'
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.absenceType){
      const absenceType = formValues.absenceType;
      const filter : IFilter = {
        field: 'absenceType',
        operator: 'eq',
        value: absenceType.toString()
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.absenceState != null){
      const absenceState = formValues.absenceState;
      const filter : IFilter = {
        field: 'absenceState',
        operator: 'eq',
        value: absenceState.toString()
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.from){
      const from = formValues.from;
      const filter : IFilter = {
        field: 'dateFrom',
        operator: 'gte',
        value: from
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.to){
      const to = formValues.to;
      const filter : IFilter = {
        field: 'dateTo',
        operator: 'lte',
        value: to
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    this.absenceGroupStore.searchAbseceGroupRequest.pageNumber = 1;

    this.onGridReload();
  }

  onResetFilter(){
    this.filterForm.reset();
    this.initFilterForm();
    delete this.absenceGroupStore.searchAbseceGroupRequest['advancedFilter'];
    this.onGridReload();
  }
}
